/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import store from '../store/store.js'

Vue.use(Router);

const originalPush = Router.prototype.push
const originalReplace = Router.prototype.replace

Router.prototype.push = function push(location) {
    return originalPush.call(this, location).catch(err => {
        if (err.name !== 'NavigationDuplicated' && !err.message.includes('Redirected')) {
            throw err
        }
    })
}
Router.prototype.replace = function replace(location) {
    return originalReplace.call(this, location).catch(err => {
        if (err.name !== 'NavigationDuplicated' && !err.message.includes('Redirected')) {
            throw err;
        }
    });
};

const router = new Router({
    mode: 'history',
    base: '/',
    routes: [
        {
            // =============================================================================
            // PAGE ROUTES
            // =============================================================================
            path: '/page',
            component: () => import('../layouts/full-page/FullPage.vue'),
            children: [
                {
                    path: '/page/privacy',
                    name: 'page.privacy',
                    component: () => import('../views/pages/page/Privacy.vue'),
                }
            ]
        },
        {
            // =============================================================================
            // BACKEND AUTH ROUTES
            // =============================================================================
            path: '/system',
            component: () => import('../layouts/main/Main.vue'),
            meta: {
                requiresAuth: true
            },
            children: [
                {
                    path: '/system/dashboard',
                    name: 'navigation.dashboard',
                    component: () => import('../views/pages/dashboard/Dashboard.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Panel', active: true},
                        ],
                        pageTitle: 'Panel',
                    }

                }, {
                    path: '/system/organization/organizations',
                    name: 'navigation.organization.organizations',
                    component: () => import('../views/pages/organization/organizations/Organizations.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Organizations', active: true},
                        ],
                        pageTitle: 'Organizations',
                    }
                }, {
                    path: '/system/organization/organizations/create',
                    name: 'navigation.organization.organizations.create',
                    component: () => import('../views/pages/organization/organizations/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Organizations', url: '/system/organization/organizations'},
                            {title: 'Create', active: true},
                        ],
                        pageTitle: 'Organization',
                    }
                }, {
                    path: '/system/organization/organizations/edit/:id',
                    name: 'navigation.organization.organizations.edit',
                    component: () => import('../views/pages/organization/organizations/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Organizations', url: '/system/organization/organizations'},
                            {title: 'Edit', active: true},
                        ],
                        pageTitle: 'Organization',
                    }
                }, {
                    path: '/system/organization/users',
                    name: 'navigation.organization.users',
                    component: () => import('../views/pages/organization/users/Users.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Organizations', url: '/system/organizations'},
                            {title: 'Users', active: true},
                        ],
                        pageTitle: 'Users',
                    }
                }, {
                    path: '/system/organization/users/create',
                    name: 'navigation.organization.users.create',
                    component: () => import('../views/pages/organization/users/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Organizations', url: '/system/organizations'},
                            {title: 'Users', url: '/system/organization/users'},
                            {title: 'Create', active: true},
                        ],
                        pageTitle: 'User',
                    }
                }, {
                    path: '/system/organization/users/view/:id',
                    name: 'navigation.organization.users.view',
                    component: () => import('../views/pages/organization/users/View.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Organizations', url: '/system/organizations'},
                            {title: 'Users', url: '/system/organization/users'},
                            {title: 'View', active: true},
                        ],
                        pageTitle: 'User',
                    }
                }, {
                    path: '/system/organization/users/edit/:id',
                    name: 'navigation.organization.users.edit',
                    component: () => import('../views/pages/organization/users/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Organizations', url: '/system/organizations'},
                            {title: 'Users', url: '/system/organization/users'},
                            {title: 'Edit', active: true},
                        ],
                        pageTitle: 'User',
                    }
                }, {
                    path: '/system/organization/roles',
                    name: 'navigation.organization.roles',
                    component: () => import('../views/pages/organization/roles/Roles.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Organizations', url: '/system/organizations'},
                            {title: 'Roles', active: true},
                        ],
                        pageTitle: 'Roles',
                    }
                }, {
                    path: '/system/organization/roles/create',
                    name: 'navigation.organization.roles.create',
                    component: () => import('../views/pages/organization/roles/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Organizations', url: '/system/organizations'},
                            {title: 'Roles', url: '/system/organization/roles'},
                            {title: 'Create', active: true},
                        ],
                        pageTitle: 'Role',
                    }
                }, {
                    path: '/system/organization/roles/view/:id',
                    name: 'navigation.organization.roles.view',
                    component: () => import('../views/pages/organization/roles/View.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Organizations', url: '/system/organizations'},
                            {title: 'Roles', url: '/system/organization/roles'},
                            {title: 'View', active: true},
                        ],
                        pageTitle: 'Role',
                    }
                }, {
                    path: '/system/organization/roles/edit/:id',
                    name: 'navigation.organization.roles.edit',
                    component: () => import('../views/pages/organization/roles/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Organizations', url: '/system/organizations'},
                            {title: 'Roles', url: '/system/organization/roles'},
                            {title: 'Edit', active: true},
                        ],
                        pageTitle: 'Role',
                    }
                }, {
                    path: '/system/clients',
                    name: 'navigation.clients',
                    component: () => import('../views/pages/clients/Clients.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Clients', active: true},
                        ],
                        pageTitle: 'Clients',
                    }
                }, {
                    path: '/system/clients/create',
                    name: 'navigation.clients.create',
                    component: () => import('../views/pages/clients/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Clients', url: '/system/clients'},
                            {title: 'Create', active: true},
                        ],
                        pageTitle: 'Client',
                    }
                }, {
                    path: '/system/clients/edit/:id',
                    name: 'navigation.clients.edit',
                    component: () => import('../views/pages/clients/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Clients', url: '/system/clients'},
                            {title: 'Edit', active: true},
                        ],
                        pageTitle: 'Client',
                    }
                }, {
                    path: '/system/payment-schedule',
                    name: 'navigation.payment_schedule',
                    component: () => import('../views/pages/paymentSchedule/PaymentSchedule.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Payment schedule', active: true},
                        ],
                        pageTitle: 'Payment schedule',
                    }
                }, {
                    path: '/system/monthly-payment-schedule',
                    name: 'navigation.monthly_payment_schedule',
                    component: () => import('../views/pages/monthlyPaymentSchedule/MonthlyPaymentSchedule.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Monthly schedule', active: true},
                        ],
                        pageTitle: 'Monthly schedule',
                    }
                }, {
                    path: '/system/client-acquisition',
                    name: 'navigation.client_acquisition',
                    component: () => import('../views/pages/clientAcquisition/ClientAcquisition.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Client acquisition', active: true},
                        ],
                        pageTitle: 'Client acquisition',
                    }
                }, {
                    path: '/system/create-invoice',
                    name: 'navigation.create_invoice',
                    component: () => import('../views/pages/clients/Clients.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Create an invoice', active: true},
                        ],
                        pageTitle: 'Invoice',
                    }
                }, {
                    path: '/system/implementations',
                    name: 'navigation.implementations',
                    component: () => import('../views/pages/implementations/Implementations.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Pre-sale', active: true},
                        ],
                        pageTitle: 'Pre-sale',
                    }
                }, {
                    path: '/system/payment-purpose',
                    name: 'navigation.payment_purpose',
                    component: () => import('../views/pages/paymentPurpose/PaymentPurposes.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Payment purpose', active: true},
                        ],
                        pageTitle: 'Payment purpose',
                    }
                }, {
                    path: '/system/sources',
                    name: 'navigation.sources',
                    component: () => import('../views/pages/sources/Sources.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Sources', active: true},
                        ],
                        pageTitle: 'Sources',
                    }
                }, {
                    path: '/system/client-type',
                    name: 'navigation.client_type',
                    component: () => import('../views/pages/clientType/ClientType.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Client types', active: true},
                        ],
                        pageTitle: 'Client types',
                    }
                }, {
                    path: '/system/email-basic',
                    name: 'navigation.email_basic',
                    component: () => import('../views/pages/emailBasics/EmailBasics.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Email templates', active: true},
                        ],
                        pageTitle: 'Email templates',
                    }
                },
                // {
                //     path: '/system/settings/general',
                //     name: 'navigation.general',
                //     component: () => import('../views/pages/settings/General.vue'),
                //     meta: {
                //         breadcrumb: [
                //             {title: 'Dashboard', url: '/'},
                //             {title: 'navigation.settings'},
                //             {title: 'navigation.general', active: true},
                //         ],
                //         pageTitle: 'navigation.general',
                //     }
                // },
                // {
                //     path: '/system/settings/languages',
                //     name: 'navigation.languages',
                //     component: () => import('../views/pages/settings/Languages.vue'),
                //     meta: {
                //         breadcrumb: [
                //             {title: 'Dashboard', url: '/'},
                //             {title: 'navigation.settings'},
                //             {title: 'navigation.languages', active: true},
                //         ],
                //         pageTitle: 'navigation.languages',
                //     }
                // },
                // {
                //     path: '/system/settings/translations',
                //     name: 'navigation.translations',
                //     component: () => import('../views/pages/settings/Translations.vue'),
                //     meta: {
                //         breadcrumb: [
                //             {title: 'Dashboard', url: '/'},
                //             {title: 'navigation.settings'},
                //             {title: 'navigation.translations', active: true},
                //         ],
                //         pageTitle: 'navigation.translations',
                //     }
                // },
                {
                    path: '/system/actions',
                    name: 'navigation.actions',
                    component: () => import('../views/pages/actions/Actions.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Actions', active: true},
                        ],
                        pageTitle: 'Actions',
                    }
                }, {
                    path: '/system/keywords',
                    name: 'navigation.keywords',
                    component: () => import('../views/pages/keywords/Keywords.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Keywords', active: true},
                        ],
                        pageTitle: 'Keywords',
                    }
                }, {
                    path: '/system/budgets',
                    name: 'navigation.budgets',
                    component: () => import('../views/pages/google/Budgets.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Advertising accounts', active: true},
                        ],
                        pageTitle: 'Advertising accounts',
                    }
                }, {
                    path: '/system/accounts',
                    name: 'navigation.accounts',
                    component: () => import('../views/pages/google/Accounts.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Advertising settings', active: true},
                        ],
                        pageTitle: 'Advertising settings',
                    }
                }, {
                    path: '/system/communications-overview',
                    name: 'navigation.communications',
                    component: () => import('../views/pages/google/CommunicationsOverview.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Productivity', active: true},
                        ],
                        pageTitle: 'Productivity',
                    }
                }, {
                    path: '/system/activity-hub',
                    name: 'navigation.activity_hub',
                    component: () => import('../views/pages/google/ActivityHub.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Activity Hub', active: true},
                        ],
                        pageTitle: 'Activity Hub',
                    }
                }, {
                    path: '/system/activity',
                    name: 'navigation.activity',
                    component: () => import('../views/pages/google/Activity.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Activity', active: true},
                        ],
                        pageTitle: 'Activity',
                    }
                }, {
                    path: '/system/google-account/edit/:id?',
                    name: 'navigation.accounts.edit',
                    component: () => import('../views/pages/google/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Advertising accounts', url: '/system/accounts'},
                            {title: 'Edit', active: true},
                        ],
                        pageTitle: 'Account',
                    }
                }, {
                    path: '/system/ads-info/:id/:timestamp?',
                    name: 'ADS Info',
                    component: () => import('../views/pages/google/Errors.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Advertising accounts', url: '/system/accounts'},
                            {title: 'Advertising account Info', active: true},
                        ],
                        pageTitle: 'Advertising account Info',
                    }
                }, {
                    // Communication Intelligence (deploy 2026-08-19)
                    path: '/system/communication-intelligence',
                    redirect: '/system/communication-intelligence/dashboard',
                }, {
                    path: '/system/communication-intelligence/dashboard',
                    name: 'navigation.communication.dashboard',
                    component: () => import('../views/pages/communication/Dashboard.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Komunikacija', active: true},
                        ],
                        pageTitle: 'Komunikacija — Parskats',
                    }
                }, {
                    path: '/system/communication-intelligence/clients',
                    name: 'navigation.communication.clients',
                    component: () => import('../views/pages/communication/Clients.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Komunikacija', url: '/system/communication-intelligence/dashboard'},
                            {title: 'Klientu komunikacija', active: true},
                        ],
                        pageTitle: 'Komunikacija — Klientu komunikacija',
                    }
                }, {
                    // 2026-08-19 (Janis): jauns URL zem Organization > Users, tikai admin+Vita+Katrīna
                    path: '/system/organization/users/all-communications',
                    name: 'navigation.organization.all_communications',
                    component: () => import('../views/pages/communication/Clients.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Users', url: '/system/organization/users'},
                            {title: 'All communications', active: true},
                        ],
                        pageTitle: 'All communications',
                    }
                }, {
                    path: '/system/communication-intelligence/managers',
                    name: 'navigation.communication.managers',
                    component: () => import('../views/pages/communication/Managers.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Komunikacija', url: '/system/communication-intelligence/dashboard'},
                            {title: 'Menedzeru aktivitate', active: true},
                        ],
                        pageTitle: 'Komunikacija — Menedzeru aktivitate',
                    }
                }, {
                    path: '/system/communication-intelligence/client/:client_id',
                    name: 'navigation.communication.client_detail',
                    component: () => import('../views/pages/communication/ClientDetail.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Komunikacija', url: '/system/communication-intelligence/dashboard'},
                            {title: 'Klienta komunikacija', active: true},
                        ],
                        pageTitle: 'Komunikacija — Klienta detalas',
                    }
                }, {
                    path: '/system/profile',
                    name: 'navigation.profile',
                    component: () => import('../views/pages/profile/Profile.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'Dashboard', url: '/'},
                            {title: 'Profile', active: true},
                        ],
                        pageTitle: 'Profile',
                    }
                },
            ]
        },
        {
            // =============================================================================
            // STATIC ROUTES
            // =============================================================================
            path: '/system',
            component: () => import('../layouts/full-page/FullPage.vue'),
            meta: {
                requiresAuth: false
            },
            children: [
                {
                    path: '/system/login/:locale?',
                    name: 'page.login',
                    component: () => import('../views/pages/auth/Login.vue'),
                },
                {
                    path: '/system/create-password/:locale?/:token',
                    name: 'page.create_password',
                    component: () => import('../views/pages/auth/CreatePassword.vue')
                },
                {
                    path: '/system/forgot-password/:locale?',
                    name: 'page.forgot_password',
                    component: () => import('../views/pages/auth/ForgotPassword.vue')
                },
                {
                    path: '/system/reset-password/:locale?/:token',
                    name: 'page.reset_password',
                    component: () => import('../views/pages/auth/ResetPassword.vue')
                },
                {
                    path: '/system/change-password/:locale?/:token',
                    name: 'page.change_password',
                    component: () => import('../views/pages/auth/ChangePassword.vue')
                },
                {
                    path: '/system/change-email/:locale?/:token',
                    name: 'page.change_email',
                    component: () => import('../views/pages/auth/ChangeEmail.vue')
                },
                {
                    path: '/system/confirm/:locale?',
                    name: 'page.confirm',
                    component: () => import('../views/pages/auth/Confirm.vue')
                },
                {
                    path: '/system/sign-up/:locale?',
                    name: 'page.sign_up',
                    component: () => import('../views/pages/auth/SignUp.vue')
                },
                // Redirect to login page, if no match found
                {
                    path: '*',
                    redirect: '/system/login'
                }
            ]
        }
    ],

    scrollBehavior(to, from, savedPosition) {
        return {x: 0, y: 0};
    }
});

router.beforeEach((to, from, next) => {

    const userIsLoggedIn = !!(store.getters.isLoggedIn);
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth);

    if (requiresAuth) {
        if (userIsLoggedIn) {
            next();
        } else {
            next({ name: 'page.login' });
        }
    }
    else {
        const isAuthPage =
            to.name === 'page.login' ||
            to.name === 'page.forgot_password' ||
            to.name === 'page.reset_password' ||
            to.name === 'page.create_password' ||
            to.name === 'page.change_password' ||
            to.name === 'page.change_email' ||
            to.name === 'page.confirm';

        if (userIsLoggedIn && isAuthPage) {
            next({ name: 'navigation.dashboard' });
        } else {
            next();
        }
    }
});

export default router
